// src/components/customers/TechnicalNotesPanel.tsx
// "6.0 + 15 gr di 6.1 + 20 volumi, iniziare da dietro": quando la cliente torna
// la formula deve essere lì, senza cercarla. Si scrive e si salva da solo;
// la nota di oggi si aggiorna, quelle dei giorni scorsi restano nello storico.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Trash2, CornerUpLeft, History, ChevronDown, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

import { useTechnicalNotes } from '@/contexts/TechnicalNotesContext';
import { useAuth } from '@/contexts/AuthContext';

interface TechnicalNotesPanelProps {
    clientId?: string | null;
    /** 'full' nella scheda cliente, 'compact' dentro il dettaglio prenotazione */
    variant?: 'full' | 'compact';
}

const todayISO = () => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatNoteDate = (isoDate: string) => {
    try {
        return format(new Date(`${isoDate}T00:00:00`), 'dd/MM/yyyy', { locale: it });
    } catch {
        return isoDate;
    }
};

const TechnicalNotesPanel: React.FC<TechnicalNotesPanelProps> = ({
    clientId,
    variant = 'full',
}) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getNotesForClient, saveNoteForClient, removeNote, isHidden } = useTechnicalNotes();

    const notes = useMemo(() => getNotesForClient(clientId), [getNotesForClient, clientId]);
    const today = todayISO();

    const myTodayNote = useMemo(
        () =>
            notes.find(
                (n) => n.note_date === today && String(n.author_id || '') === String(user?.id || '')
            ),
        [notes, today, user?.id]
    );

    // Le note dei giorni scorsi (o di altri colleghi) restano consultabili nello storico
    const previousNotes = useMemo(
        () => notes.filter((n) => n.id !== myTodayNote?.id && (n.content || '').trim()),
        [notes, myTodayNote?.id]
    );

    // Ultimo intervento sulle note di questo cliente: quando e da chi
    const lastEdit = useMemo(() => {
        const withContent = notes.filter((n) => (n.content || '').trim());
        if (withContent.length === 0) return null;
        return withContent.reduce((latest, n) => {
            const a = new Date(n.updated_at || n.created_at).getTime();
            const b = new Date(latest.updated_at || latest.created_at).getTime();
            return a > b ? n : latest;
        });
    }, [notes]);

    const [draft, setDraft] = useState(myTodayNote?.content || '');
    const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [showHistory, setShowHistory] = useState(variant === 'full');
    const [isRevealed, setIsRevealed] = useState(false);
    const lastSavedRef = useRef(myTodayNote?.content || '');
    const clientRef = useRef(clientId);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    // Una volta aperto il campo resta aperto: svuotandolo non deve sparire sotto le dita.
    const wasShownRef = useRef(false);

    // Cambiando cliente il pannello riparte dalla nota di oggi di quel cliente
    useEffect(() => {
        if (clientRef.current !== clientId) {
            clientRef.current = clientId;
            setDraft(myTodayNote?.content || '');
            lastSavedRef.current = myTodayNote?.content || '';
            setStatus('idle');
            setIsRevealed(false);
            wasShownRef.current = false;
        }
    }, [clientId, myTodayNote?.content]);

    // Salvataggio automatico: un secondo di pausa nella scrittura e va giù
    useEffect(() => {
        if (!clientId || isHidden) return;
        if (draft === lastSavedRef.current) return;
        if (!draft.trim() && !myTodayNote) return;

        const timer = setTimeout(async () => {
            setStatus('saving');
            const res = await saveNoteForClient(clientId, draft);
            if (res.success) {
                lastSavedRef.current = draft;
                setStatus('saved');
            } else {
                setStatus('error');
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, [draft, clientId, isHidden, myTodayNote, saveNoteForClient]);

    if (isHidden || !clientId) return null;

    // Nel dettaglio prenotazione, finché non c'è niente da mostrare resta solo la
    // scritta con il +: il campo compare quando serve davvero.
    const hasContent = !!draft.trim() || previousNotes.length > 0;
    const showEditor = variant === 'full' || hasContent || isRevealed || wasShownRef.current;
    wasShownRef.current = showEditor;

    if (!showEditor) {
        return (
            <button
                type="button"
                onClick={() => {
                    setIsRevealed(true);
                    requestAnimationFrame(() => textareaRef.current?.focus());
                }}
                className="inline-flex items-center gap-1.5 text-sm font-bold text-clip-500 hover:text-clip-600 transition-colors"
            >
                <Plus size={16} />
                {t('customers.technical_notes.add', 'Aggiungi note cliente')}
            </button>
        );
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider">
                    {t('customers.technical_notes.title', 'Note cliente')}
                </label>
                <span className="text-xs text-gray-400 flex items-center gap-1.5 h-4">
                    {status === 'saving' && (
                        <>
                            <Loader2 size={12} className="animate-spin" />
                            {t('common.saving', 'Salvataggio…')}
                        </>
                    )}
                    {status === 'saved' && (
                        <>
                            <Check size={12} className="text-emerald-500" />
                            {t('customers.technical_notes.saved', 'Salvato')}
                        </>
                    )}
                    {status === 'error' && (
                        <span className="text-red-500">{t('common.error', 'Errore')}</span>
                    )}
                </span>
            </div>

            {/* Altezza fissa: se la formula è lunga scorre, non spinge giù il resto */}
            <textarea
                ref={textareaRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t(
                    'customers.technical_notes.placeholder',
                    'Scrivi qui le note sul cliente: trattamenti, prodotti, preferenze…'
                )}
                className={`w-full ${variant === 'compact' ? 'h-24' : 'h-28'
                    } resize-none overflow-y-auto custom-scrollbar p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none font-medium transition-all placeholder:text-gray-400 text-gray-900`}
            />

            {lastEdit && (
                <p className="text-[11px] text-gray-400">
                    {t('customers.technical_notes.last_edit', 'Ultima modifica: {{date}}', {
                        date: format(new Date(lastEdit.updated_at || lastEdit.created_at), 'dd/MM/yyyy HH:mm', { locale: it }),
                    })}
                    {lastEdit.author_name ? ` · ${lastEdit.author_name}` : ''}
                </p>
            )}

            {previousNotes.length > 0 && (
                <div>
                    <button
                        type="button"
                        onClick={() => setShowHistory((v) => !v)}
                        className="text-xs font-semibold text-gray-400 hover:text-gray-600 inline-flex items-center gap-1.5 transition-colors"
                    >
                        <History size={12} />
                        {t('customers.technical_notes.history', 'Storico')} ({previousNotes.length})
                        <ChevronDown
                            size={12}
                            className={`transition-transform ${showHistory ? 'rotate-180' : ''}`}
                        />
                    </button>

                    {showHistory && (
                        <ul className="mt-2 space-y-2 max-h-40 overflow-y-auto custom-scrollbar pr-1">
                            {previousNotes.map((note) => (
                                <li
                                    key={note.id}
                                    className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-[11px] font-semibold text-gray-500">
                                            {formatNoteDate(note.note_date)}
                                            {note.author_name ? ` · ${note.author_name}` : ''}
                                        </span>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => setDraft(note.content)}
                                                className="text-gray-400 hover:text-clip-600 transition-colors"
                                                title={t('customers.technical_notes.reuse', 'Riprendi questa formula')}
                                            >
                                                <CornerUpLeft size={12} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => removeNote(note.id)}
                                                className="text-gray-400 hover:text-red-600 transition-colors"
                                                title={t('common.delete', 'Elimina')}
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">
                                        {note.content}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default TechnicalNotesPanel;
